import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '../services/api'

/**
 * Retrieves an Auth0 access token and injects it into the API client.
 *
 * On silent-auth failure (expired/invalid refresh token), redirects to Auth0
 * login and returns the user to the current page afterward. This handles the
 * common case where a long-idle session can no longer be silently renewed.
 *
 * Returns `{ ready }` — true once the token is set and API calls can proceed.
 */
export function useApiAuth() {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) return

    getAccessTokenSilently()
      .then((token) => {
        apiClient.setAuthToken(token)
        setReady(true)
      })
      .catch((err: unknown) => {
        console.error('Silent token refresh failed, redirecting to login:', err)
        loginWithRedirect({ appState: { returnTo: window.location.pathname } })
      })
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect])

  return { ready }
}
